import { MouseEvent, ReactNode, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon24Swap } from '@figma/fpl-icons';
import { API_ROUTES } from '@/api/apiRoutes';
import { safeAxios } from '@/services/safeAxios';
import { Box } from '@/UI/Box/Box';
import { AppContextMenu } from '@/UI/AppContextMenu/AppContextMenu';
import { AppToggleButton } from '@/UI/AppToggleButtons/AppToggleButton';
import { log } from '@/logger/logger.ts';
import { CreditsContext } from '@/services/CreditsContext';
import { I18N_KEYS } from '@/language/keys';
import { FlexCenVer, FlexCenVerSpaceBetween } from '@/UI/styles';
import { SubscriptionType } from '@/types/shared';
import { BaseWorkspace } from '@/types/auth.types';
import { PermissionsContainer } from '@/components/PermissionsContainer/PermissionsContainer';
import { ButtonTextPlain } from '@/UI/ButtonTextPlain/ButtonTextPlain';
import { EllipsisText } from '@/UI/EllipsisText/EllipsisText';
import useWorkspacesStore from '@/state/workspaces.state';
import { AsteriskIcon } from '@/UI/Icons/AsteriskIcon';
import { SortIcon } from '@/UI/Icons';
import { formatNumberWithCommas } from '@/utils/numbers';
import { useSubscriptionPermissions } from '@/hooks/useSubscriptionPermissions/useSubscriptionPermissions';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import Actions, { MenuAction } from '../../Menu/Actions';
import { SubscriptionSection } from './SubscriptionSection';
import { WorkspaceIcon } from './WorkspaceIcon';
import { WorkspacesMenu } from './WorkspacesMenu/WorkspacesMenu';

const logger = log.getLogger('UserMenu');

const WorkspaceSection = ({
  showSwitch,
  onClickSwitch,
  isWorkspacesMenuOpen,
}: {
  showSwitch: boolean;
  onClickSwitch: (event: MouseEvent<HTMLElement>) => void;
  isWorkspacesMenuOpen: boolean;
}) => {
  const isFplMigrationEnabled = useFplMigrationFlag();
  const workspaceName = useWorkspacesStore((state) => state.activeWorkspace.workspaceName);

  return (
    <FlexCenVerSpaceBetween sx={{ height: '40px', p: 1 }}>
      <FlexCenVer sx={{ gap: 1 }}>
        <WorkspaceIcon text={workspaceName} />
        <EllipsisText maxWidth="130px" variant="body-sm-rg">
          {workspaceName}
        </EllipsisText>
      </FlexCenVer>
      {showSwitch && (
        <AppToggleButton
          value="switch-workspace"
          isIcon
          btnW={24}
          btnH={24}
          onClick={onClickSwitch}
          selected={isWorkspacesMenuOpen}
          aria-label="Switch workspace"
          aria-haspopup="menu"
          sx={{ border: 'none' }}
        >
          {isFplMigrationEnabled ? <Icon24Swap /> : <SortIcon style={{ transform: 'rotate(90deg)' }} />}
        </AppToggleButton>
      )}
    </FlexCenVerSpaceBetween>
  );
};

interface UserMenuProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  settings: MenuAction[];
}

export const UserMenu = ({ anchorEl, onClose, settings }: UserMenuProps) => {
  const { credits, workspaceCredits, openUpgradeModal, handleGetMoreCreditsClick } = useContext(CreditsContext);
  const { t } = useTranslation();
  const isFplMigrationEnabled = useFplMigrationFlag();
  const [anchorElWorkspaces, setAnchorElWorkspaces] = useState<HTMLElement | null>(null);

  const { activeWorkspace, workspaces } = useWorkspacesStore();
  const { isSubscriptionTypeIn, isWorkspaceAdmin } = useSubscriptionPermissions();

  const getCreditsHintText = useMemo(() => {
    if (isSubscriptionTypeIn([SubscriptionType.Team]) && !isWorkspaceAdmin && credits === 0) {
      return t(I18N_KEYS.MAIN_DASHBOARD.USERMENU.TEAM_HINT_CREDITS_EMPTY);
    }
    return '';
  }, [isWorkspaceAdmin, credits, t, isSubscriptionTypeIn]);

  const handleOpenWorkspacesMenu = (event: MouseEvent<HTMLElement>) => setAnchorElWorkspaces(event.currentTarget);

  const handleCloseWorkspacesMenu = () => setAnchorElWorkspaces(null);

  const onSwitchWorkspace = async (workspace: BaseWorkspace) => {
    try {
      const res = await safeAxios.put(API_ROUTES.users.setActiveWorkspace(), {
        workspaceId: workspace.workspaceId,
      });
      if (res.status === 200) {
        window.location.reload();
      }
    } catch (e) {
      logger.error('Error setting active workspace.', e);
    }
  };

  const onCreditsCta = () => {
    handleGetMoreCreditsClick();
    onClose();
  };

  const onPlanCta = () => {
    openUpgradeModal();
    onClose();
  };

  const getCreditsCtaEl = (): ReactNode => {
    const isFreePlan = isSubscriptionTypeIn([SubscriptionType.Free]);
    const text = isFreePlan
      ? t(I18N_KEYS.MAIN_DASHBOARD.USERMENU.CTA_CREDITS_FREE)
      : t(I18N_KEYS.MAIN_DASHBOARD.USERMENU.CTA_CREDITS);
    const permission = isFreePlan ? 'plan' : 'credits';

    return (
      <PermissionsContainer permission={permission} type="action">
        <ButtonTextPlain onClick={onCreditsCta} variant="body-sm-rg">
          {text}
        </ButtonTextPlain>
      </PermissionsContainer>
    );
  };

  const getPlanCtaEl = (): ReactNode => {
    if (isSubscriptionTypeIn([SubscriptionType.Team])) {
      return null;
    }
    return (
      <PermissionsContainer permission="plan" type="action">
        <ButtonTextPlain onClick={onPlanCta} variant="body-sm-rg">
          {t(I18N_KEYS.MAIN_DASHBOARD.USERMENU.CTA_PLAN)}
        </ButtonTextPlain>
      </PermissionsContainer>
    );
  };

  return (
    <>
      <AppContextMenu
        anchorEl={anchorEl}
        marginThreshold={8}
        offset={{ top: '4px' }}
        open={Boolean(anchorEl)}
        onClose={onClose}
      >
        <Box component="li" role="presentation" sx={{ minWidth: 200, px: isFplMigrationEnabled ? '4px' : 0 }}>
          <WorkspaceSection
            showSwitch={workspaces?.length > 1}
            isWorkspacesMenuOpen={Boolean(anchorElWorkspaces)}
            onClickSwitch={handleOpenWorkspacesMenu}
          />
          <PermissionsContainer permission="credits">
            <SubscriptionSection
              title={
                activeWorkspace?.subscription?.type === SubscriptionType.Team ||
                activeWorkspace?.subscription?.type === SubscriptionType.Enterprise
                  ? t(I18N_KEYS.MAIN_DASHBOARD.USERMENU.TEAM_CREDITS)
                  : t(I18N_KEYS.MAIN_DASHBOARD.USERMENU.CREDITS)
              }
              value={formatNumberWithCommas(
                activeWorkspace?.subscription?.type === SubscriptionType.Team ||
                  activeWorkspace?.subscription?.type === SubscriptionType.Enterprise
                  ? workspaceCredits || 0
                  : credits || 0,
              )}
              icon={<AsteriskIcon />}
              hintText={getCreditsHintText}
              ctaEl={getCreditsCtaEl()}
            />
          </PermissionsContainer>

          <PermissionsContainer permission="plan">
            <SubscriptionSection
              title={t(I18N_KEYS.MAIN_DASHBOARD.USERMENU.PLAN)}
              value={activeWorkspace?.subscription?.name}
              ctaEl={getPlanCtaEl()}
            />
          </PermissionsContainer>
        </Box>
        <Actions items={settings} setAnchorEl={onClose} />
      </AppContextMenu>
      <WorkspacesMenu
        workspaces={workspaces}
        anchorEl={anchorElWorkspaces}
        activeWorkspace={activeWorkspace}
        onSelect={onSwitchWorkspace}
        onClose={handleCloseWorkspacesMenu}
      />
    </>
  );
};
